import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { OpeningHoursSubformComponent } from 'src/app/shared/components/opening-hours-subform/opening-hours-subform.component';
import { CanComponentDeactivate } from 'src/app/shared/guards/can-deactivate.guard';
import { ApiErrorResponse } from 'src/app/shared/interfaces/api-error-response';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { isPhoneNumber } from 'src/app/shared/validators/phone-number.validator';
import { SubSink } from 'subsink';

import {
  AddressViewModel,
  MerchantContactsViewModel,
} from '@app/api/marketplace/models';
import { EasyAddressSubformComponent } from '@shared/components/easy-address-subform/easy-address-subform.component';
import { LocalizedValueSubformComponent } from '@shared/components/localized-value-subform/localized-value-subform.component';
import { asNonNullArray } from '@shared/helpers/array-helper';
import { processErrorResponse } from '@shared/helpers/form-helper';
import { trimInput } from '@shared/helpers/input-trim-helper';
import { isValidEmail } from '@shared/validators/email.validator';

import { MerchantActions, MerchantSelectors } from '../../store/merchant.slice';
import { OtherContactsSubformComponent } from '../other-contacts-subform/other-contacts-subform.component';

@Component({
  selector: 'app-merchant-contact-data',
  templateUrl: './merchant-contact-data.component.html',
  styleUrls: ['./merchant-contact-data.component.scss'],
})
export class MerchantContactDataComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  TITLE = 'MERCHANT.TITLE.';
  PLACEHOLDER = 'MERCHANT.PLACEHOLDER.';
  HINT = 'MERCHANT.HINT.';
  REQUIRED = this.HINT + 'REQUIRED';

  showVisibleFields: boolean = false;

  isLoaded$: Observable<boolean> = this.store.select(
    MerchantSelectors.isContactsLoaded
  );
  hasLoadError: Observable<boolean> = this.store.select(
    MerchantSelectors.selectContactsLoadError
  );
  data$: Observable<MerchantContactsViewModel | undefined> = this.store.select(
    MerchantSelectors.selectContacts
  );
  isUpdating$: Observable<boolean> = this.store.select(
    MerchantSelectors.selectContactsUpdating
  );
  updateError$: Observable<ApiErrorResponse | undefined> = this.store.select(
    MerchantSelectors.selectContactsUpdateError
  );

  // basic data address
  isBasicDataLoaded$: Observable<boolean> = this.store.select(
    MerchantSelectors.isBasicDataLoaded
  );
  basicDataAddress$: Observable<AddressViewModel | undefined> =
    this.store.select(MerchantSelectors.basicDataAddress);

  private subs = new SubSink();

  emailSubformValidators = [Validators.required, isValidEmail];
  phoneSubformValidators = [Validators.required, isPhoneNumber];

  form = this.fb.group({
    postalAddress: EasyAddressSubformComponent.buildAddressForm(this.fb),
    openingHours: OpeningHoursSubformComponent.buildOpeningHoursForm(this.fb),
    emails: new UntypedFormArray([]),
    phones: new UntypedFormArray([]),
    others: OtherContactsSubformComponent.buildOtherContactsForm(this.fb),
    visibleFields: this.fb.group({
      postalAddress: new UntypedFormControl(false),
      openingHours: new UntypedFormControl(false),
      emails: new UntypedFormControl(false),
      phones: new UntypedFormControl(false),
      others: new UntypedFormControl(false),
    }),
  });

  constructor(
    private readonly store: Store,
    private readonly fb: UntypedFormBuilder,
    private readonly dialogService: DialogService
  ) {}

  ngOnInit() {
    this.subs.sink = this.isBasicDataLoaded$.subscribe((loaded) => {
      if (!loaded) this.store.dispatch(MerchantActions.loadBasicData.trigger());
    });
    this.subs.sink = this.data$.subscribe((data) =>
      data ? this.onData(data) : null
    );
    this.subs.sink = this.isLoaded$.subscribe((loaded) => {
      if (!loaded) this.onLoad();
    });
    this.subs.sink = this.updateError$.subscribe((response) =>
      processErrorResponse(this.form, response)
    );
    this.subs.sink = this.isUpdating$.subscribe((updating) =>
      this.updating(!updating)
    );
  }

  updating(done: boolean): void {
    if (done) {
      this.form.markAsPristine();
    }
  }

  onLoad() {
    this.store.dispatch(MerchantActions.loadContacts.trigger());
  }

  onData(data: MerchantContactsViewModel) {
    let initialFormValue = this.form.value;
    this.form.reset(initialFormValue);
    if ((this.form.get('emails') as UntypedFormArray).length == 0)
      // only execute when the form isn't built yet
      this.form.setControl(
        'emails',
        new UntypedFormArray([
          ...data.emails.map((email) => {
            return new UntypedFormGroup({
              title: LocalizedValueSubformComponent.buildForm(email.title),
              email: new UntypedFormControl(
                email.email,
                this.emailSubformValidators
              ),
            });
          }),
        ])
      );
    if ((this.form.get('phones') as UntypedFormArray).length == 0)
      // only execute when the form isn't built yet
      this.form.setControl(
        'phones',
        new UntypedFormArray([
          ...data.phones.map((phone) => {
            return new UntypedFormGroup({
              title: LocalizedValueSubformComponent.buildForm(phone.title),
              phone: new UntypedFormControl(
                phone.phone,
                this.phoneSubformValidators
              ),
            });
          }),
        ])
      );
    this.form.patchValue({
      ...data,
    });
  }

  onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      let value = structuredClone(this.form.value);
      Object.keys(value.openingHours).forEach((day) => {
        if (!value.openingHours[day].from) {
          value.openingHours[day] = null;
        }
      });

      this.store.dispatch(
        MerchantActions.updateContacts.trigger(trimInput(value))
      );
    }
  }

  onToggleVisibleFields() {
    this.showVisibleFields = !this.showVisibleFields;
  }

  asArray = asNonNullArray;

  canDeactivate() {
    return this.form.dirty ? this.dialogService.confirmUnsavedChanges() : true;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
